import { useEffect, useState } from 'react';
import { PageHeader } from '@nimbitlabs/layouts';
import { AlertRule, alertsApi, AlertSeverity, AlertChannel, NotificationTarget } from '@/lib/alerts';
import { Plus, Pencil, Trash2, ArrowLeft, X, Mail, MessageSquare, Webhook } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { PermissionGate } from '@nimbitlabs/features';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, useToast, ErrorAlert } from '@nimbitlabs/ui';

const SEVERITY_OPTIONS: { value: AlertSeverity; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: 'bg-destructive text-destructive-foreground' },
  { value: 'warning', label: 'Warning', color: 'bg-warning text-warning-foreground' },
  { value: 'info', label: 'Info', color: 'bg-primary text-primary-foreground' },
];

const CHANNEL_OPTIONS: { 
  value: AlertChannel; 
  label: string; 
  placeholder: string; 
  helpText: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}[] = [
  { 
    value: 'email', 
    label: 'Email', 
    placeholder: 'ops@company.com, alerts@company.com',
    helpText: 'Send notifications via email. Separate multiple addresses with commas.',
    icon: Mail,
  },
  { 
    value: 'slack', 
    label: 'Slack', 
    placeholder: '#alerts, #incidents, @username',
    helpText: 'Send notifications to Slack channels or users. Use # for channels, @ for users.',
    icon: MessageSquare,
  },
  { 
    value: 'webhook', 
    label: 'Webhook', 
    placeholder: 'https://api.example.com/webhooks/alerts',
    helpText: 'Send POST requests to custom webhook URLs.',
    icon: Webhook,
    disabled: true,
  },
];

const CONDITION_OPTIONS = [
  { value: 'health_check_failed', label: 'Health Check Failed' },
];

export default function AlertRules() {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [services, setServices] = useState<API.ServiceDto[]>([]);
  const [environments, setEnvironments] = useState<API.EnvironmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<AlertRule | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    serviceId: '',
    environmentId: '',
    condition: 'health_check_failed',
    threshold: 1,
    severity: 'warning' as AlertSeverity,
    notificationTargets: [] as NotificationTarget[],
    enabled: true,
    autoCreateIncident: true,
    incidentEscalationThreshold: 5,
    criticalEscalationThreshold: 15,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [rulesData, servicesData, environmentsData] = await Promise.all([
        alertsApi.getAlertRules(),
        api.services.getAll(),
        api.environments.getAll(),
      ]);
      setRules(rulesData);
      setServices(servicesData);
      setEnvironments(environmentsData);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      serviceId: '',
      environmentId: '',
      condition: 'health_check_failed',
      threshold: 1,
      severity: 'warning',
      notificationTargets: [],
      enabled: true,
      autoCreateIncident: true,
      incidentEscalationThreshold: 5,
      criticalEscalationThreshold: 15,
    });
    setEditingRule(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (rule: AlertRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      serviceId: rule.serviceId,
      environmentId: rule.environmentId,
      condition: rule.condition,
      threshold: rule.threshold,
      severity: rule.severity,
      notificationTargets: rule.notificationTargets || [],
      enabled: rule.enabled,
      autoCreateIncident: rule.autoCreateIncident,
      incidentEscalationThreshold: rule.incidentEscalationThreshold,
      criticalEscalationThreshold: rule.criticalEscalationThreshold,
    });
    setShowDialog(true);
  };

  const handleDelete = (rule: AlertRule) => {
    setRuleToDelete(rule);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      await alertsApi.deleteAlertRule(ruleToDelete.id);
      toast({ title: 'Success', description: 'Alert rule deleted successfully' });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete alert rule',
        variant: 'destructive',
      });
    } finally {
      setShowDeleteDialog(false);
      setRuleToDelete(null);
    }
  };

  // Notification target management
  const addNotificationTarget = () => {
    const newTarget: NotificationTarget = {
      channel: 'email', // default channel
      recipients: [],
    };
    setFormData({
      ...formData,
      notificationTargets: [...formData.notificationTargets, newTarget],
    });
  };

  const removeNotificationTarget = (index: number) => {
    const updated = formData.notificationTargets.filter((_, i) => i !== index);
    setFormData({ ...formData, notificationTargets: updated });
  };

  const updateNotificationTarget = (index: number, field: 'channel' | 'recipients', value: AlertChannel | string[]) => {
    const updated = [...formData.notificationTargets];
    if (field === 'channel') {
      updated[index] = { ...updated[index], channel: value as AlertChannel };
    } else {
      updated[index] = { ...updated[index], recipients: value as string[] };
    }
    setFormData({ ...formData, notificationTargets: updated });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.serviceId || !formData.environmentId) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingRule) {
        await alertsApi.updateAlertRule(editingRule.id, formData);
        toast({ title: 'Success', description: 'Alert rule updated successfully' });
      } else {
        await alertsApi.createAlertRule(formData);
        toast({ title: 'Success', description: 'Alert rule created successfully' });
      }
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${editingRule ? 'update' : 'create'} alert rule`,
        variant: 'destructive',
      });
    }
  };


  const getSeverityBadge = (severity: string) => {
    const option = SEVERITY_OPTIONS.find(s => s.value === severity);
    return option?.color || 'bg-muted';
  };

  const getServiceName = (serviceId: string) => {
    return services.find(s => s.id === serviceId)?.name || 'Unknown Service';
  };

  const getEnvironmentName = (environmentId: string) => {
    return environments.find(e => e.id === environmentId)?.name || 'Unknown Environment';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading alert rules...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <ErrorAlert error={error} />
      <PageHeader
        title="Alert Rules"
        description="Configure alert rules for your services"
        actions={
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={() => navigate('/alerts')} className="w-full sm:w-auto">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Alerts
            </Button>
            <PermissionGate permission="alerts:create">
              <Button onClick={handleCreate} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Create Rule
              </Button>
            </PermissionGate>
          </div>
        }
      />

      <div className="grid gap-4">
        {rules.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No alert rules configured</p>
            </CardContent>
          </Card>
        ) : (
          rules.map(rule => (
            <Card key={rule.id}>
              <CardHeader>
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-lg">{rule.name}</CardTitle>
                      <Badge className={getSeverityBadge(rule.severity)}>
                        {rule.severity}
                      </Badge>
                      {rule.enabled ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-500/10 text-gray-600 border-gray-500/20">
                          Disabled
                        </Badge>
                      )}
                    </div>
                    <CardDescription>
                      {getServiceName(rule.serviceId)} • {getEnvironmentName(rule.environmentId)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <PermissionGate permission="alerts:update">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(rule)} className="flex-1 sm:flex-none">
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </PermissionGate>
                    <PermissionGate permission="alerts:delete">
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(rule)} className="flex-1 sm:flex-none">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </PermissionGate>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <span className="text-muted-foreground">Condition:</span>{' '}
                      <span className="font-medium">
                        {CONDITION_OPTIONS.find(c => c.value === rule.condition)?.label || rule.condition}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Threshold:</span>{' '}
                      <span className="font-medium">{rule.threshold}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Channels:</span>{' '}
                    {(rule.notificationTargets || []).map((target, idx) => (
                      <Badge key={idx} variant="secondary" className="ml-1">
                        {CHANNEL_OPTIONS.find(c => c.value === target.channel)?.label || target.channel}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}</DialogTitle>
            <DialogDescription>
              Configure when and how you want to be notified about service issues
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Health check failure"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service">Service *</Label>
                <Select key={`service-${formData.serviceId}`} value={formData.serviceId} onValueChange={(value) => setFormData({ ...formData, serviceId: value })}>
                  <SelectTrigger id="service">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="environment">Environment *</Label>
                <Select key={`environment-${formData.environmentId}`} value={formData.environmentId} onValueChange={(value) => setFormData({ ...formData, environmentId: value })}>
                  <SelectTrigger id="environment">
                    <SelectValue placeholder="Select environment" />
                  </SelectTrigger>
                  <SelectContent>
                    {environments.map(env => (
                      <SelectItem key={env.id} value={env.id}>
                        {env.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="condition">Condition *</Label>
              <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                <SelectTrigger id="condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="severity">Severity *</Label>
              <Select value={formData.severity} onValueChange={(value: AlertSeverity) => setFormData({ ...formData, severity: value })}>
                <SelectTrigger id="severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${option.color}`} />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Notification Channels</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure where alerts should be sent
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addNotificationTarget}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Channel
                </Button>
              </div>

              {formData.notificationTargets.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="rounded-full bg-muted p-3">
                        <Plus className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium">No notification channels</p>
                      <p className="text-sm text-muted-foreground">
                        Add at least one channel to receive alerts
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-3">
                {formData.notificationTargets.map((target, index) => {
                  const channelOption = CHANNEL_OPTIONS.find(opt => opt.value === target.channel);
                  const ChannelIcon = channelOption?.icon || Mail;
                  
                  return (
                    <Card key={index} className="border-muted bg-muted/30">
                      <CardContent className="p-4 space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-lg bg-background border border-muted p-2 mt-1">
                            <ChannelIcon className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <Label htmlFor={`channel-${index}`} className="text-sm font-medium">
                                  Channel Type
                                </Label>
                                <Select 
                                  value={target.channel} 
                                  onValueChange={(value) => updateNotificationTarget(index, 'channel', value as AlertChannel)}
                                >
                                  <SelectTrigger id={`channel-${index}`} className="mt-1.5">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {CHANNEL_OPTIONS.map(option => {
                                      const OptionIcon = option.icon;
                                      return (
                                        <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                                          <div className="flex items-center gap-2">
                                            <OptionIcon className="h-4 w-4" />
                                            <span>{option.label}</span>
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeNotificationTarget(index)}
                                className="mt-6"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor={`recipients-${index}`} className="text-sm font-medium">
                                {target.channel === 'email' && 'Email Addresses'}
                                {target.channel === 'slack' && 'Slack Targets'}
                                {target.channel === 'webhook' && 'Webhook URLs'}
                              </Label>
                              <Input
                                id={`recipients-${index}`}
                                placeholder={channelOption?.placeholder}
                                value={target.recipients.join(', ')}
                                onChange={(e) => {
                                  const recipients = e.target.value
                                    .split(',')
                                    .map(r => r.trim())
                                    .filter(r => r.length > 0);
                                  updateNotificationTarget(index, 'recipients', recipients);
                                }}
                              />
                              <p className="text-xs text-muted-foreground">
                                {channelOption?.helpText}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold">Incident Escalation Settings</h3>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="autoCreateIncident"
                  checked={formData.autoCreateIncident}
                  onCheckedChange={(checked) => setFormData({ ...formData, autoCreateIncident: checked })}
                />
                <Label htmlFor="autoCreateIncident" className="cursor-pointer">
                  Automatically create incidents for sustained failures
                </Label>
              </div>

              {formData.autoCreateIncident && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div className="space-y-2">
                    <Label htmlFor="incidentThreshold">Incident Threshold *</Label>
                    <Input
                      id="incidentThreshold"
                      type="number"
                      min="1"
                      value={formData.incidentEscalationThreshold}
                      onChange={(e) => setFormData({ ...formData, incidentEscalationThreshold: parseInt(e.target.value) || 5 })}
                      placeholder="5"
                    />
                    <p className="text-xs text-muted-foreground">
                      Consecutive failures before creating incident (~{formData.incidentEscalationThreshold * 2} min)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="criticalThreshold">Critical Threshold *</Label>
                    <Input
                      id="criticalThreshold"
                      type="number"
                      min="1"
                      value={formData.criticalEscalationThreshold}
                      onChange={(e) => setFormData({ ...formData, criticalEscalationThreshold: parseInt(e.target.value) || 15 })}
                      placeholder="15"
                    />
                    <p className="text-xs text-muted-foreground">
                      Consecutive failures before escalating to critical (~{formData.criticalEscalationThreshold * 2} min)
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
              <Label htmlFor="enabled" className="cursor-pointer">
                Enable this rule
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Alert Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{ruleToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
